import Ember from 'ember';
import { inject as service } from '@ember/service';
import Controller from '@ember/controller';
import EmObject, { action } from '@ember/object';
import { task } from 'ember-concurrency';
import moment from 'moment-timezone';
import RouterService from '@ember/routing/router-service';
import SessionService from 'cio/services/session';
import ApiService, { ApiError } from 'cio/services/api';
import AccountService from 'cio/services/account';
import SignupService, { PersonalizationSignupRoute, RegistrationAttrs, SignupRoute } from 'cio/services/signup';
import AlertsService from 'cio/services/alerts';
import AnalyticsService from 'cio/services/analytics';
import fade from 'ember-animated/transitions/fade';
import { ProductApp, productActivationInfo } from 'cio/utils/product-app-info';
import { tracked } from '@glimmer/tracking';
import ExperimentsService from 'cio/services/experiments';

function defaultSignupFormValues(defaultValues?: RegistrationAttrs) {
  return EmObject.create({
    email: defaultValues?.email ?? '',
    firstName: defaultValues?.firstName ?? '',
    lastName: defaultValues?.lastName ?? '',
    password: '',
    passwordConfirmation: '',
    companyName: '',
    role: '',
    roleOther: '',
    useCase: '',
    useCaseDetail: '',
    employeeCount: '',
    signupType: 'journeys',
    cdpPlatform: '',
    cdpPlatformOther: '',
    workPhone: '',
    acceptTerms: true,
    dataCenter: undefined,
    channels: [],
    channelsOther: '',
    automationExperience: '',
    integrationExperience: '',
    testDeliveryMode: undefined,
  });
}

const ROUTE_TRANSITION_MAP: Record<SignupRoute, SignupRoute | string> = {
  'signup.index': 'signup.password',
  'signup.password': 'signup.company',
  'signup.company': '',
  'authenticated.post_registration.goal': 'authenticated.post_registration.ready',
  'authenticated.post_registration.ready': '',
};

const PERSONALIZATION_ROUTE_MAP: Record<PersonalizationSignupRoute, PersonalizationSignupRoute | string> = {
  'signup.index': 'signup.password',
  'signup.password': 'signup.company',
  'signup.company': '',
  'authenticated.post_registration.role': 'authenticated.post_registration.goal',
  'authenticated.post_registration.goal': 'authenticated.post_registration.channels',
  'authenticated.post_registration.channels': 'authenticated.post_registration.tooling',
  'authenticated.post_registration.tooling': 'authenticated.post_registration.connect',
  'authenticated.post_registration.connect': '',
};

export default class SignupController extends Controller {
  @service router!: RouterService;
  @service session!: SessionService;
  @service api!: ApiService;
  @service account!: AccountService;
  @service signup!: SignupService;
  @service alerts!: AlertsService;
  @service analytics!: AnalyticsService;
  @service experiments!: ExperimentsService;

  declare model: {
    signupType: {
      name: ProductApp;
      isJourneys: boolean;
      isDataPipelines: boolean;
    };
    testimonials: Record<string, any>;
  };

  queryParams: [
    {
      category: 'content';
    },
    'signupType',
  ] = [{ category: 'content' }, 'signupType'];

  @tracked formValues: RegistrationAttrs = defaultSignupFormValues();

  fade = fade;
  logoColorHorizontalWhiteImage = new URL(
    'cio/assets/images/logo-color-horizontal-white.svg',
    import.meta.url,
  ).toString();

  category: string | null = null;

  get product() {
    return productActivationInfo[this.model?.signupType?.name ?? 'journeys'];
  }

  goToNextStep({ skipTracking = false } = {}) {
    if (this.formValues.testDeliveryMode == null) {
      let isTestDeliveryModeEnabled = this.experiments.getExperimentVariation('testDeliveryMode') === 'treatment';

      // Disable by default for testing, as this value is random, so it could cause flakiness
      if (Ember.testing) {
        isTestDeliveryModeEnabled = false;
      }

      this.setFormValue('testDeliveryMode', isTestDeliveryModeEnabled);
    }

    if (this.model.signupType.isDataPipelines) {
      let { currentRouteName } = this.router as { currentRouteName: SignupRoute };
      if (!skipTracking) {
        this.trackSignupProgress(currentRouteName); // tracking call - don't wait for API response
      }

      let nextRouteName = ROUTE_TRANSITION_MAP[currentRouteName];
      if (nextRouteName) {
        this.router.transitionTo(nextRouteName);
      }
    } else {
      let { currentRouteName } = this.router as { currentRouteName: PersonalizationSignupRoute };
      if (!skipTracking) {
        this.trackSignupProgress(currentRouteName); // tracking call - don't wait for API response
      }

      let nextRouteName = PERSONALIZATION_ROUTE_MAP[currentRouteName];
      if (nextRouteName) {
        this.router.transitionTo(nextRouteName);
      }
    }
  }

  signupTask = task(async () => {
    this.trackSignupProgress(); // tracking call - don't wait for API response

    try {
      this.setFormValue('signupType', this.model.signupType.name);
      let { account, user, accessToken, sessionExpiresAt } = await this.signup.register({ values: this.formValues });
      await this.session.authenticate('authenticator:access-token', { accessToken, sessionExpiresAt });

      return { account, user };
    } catch (err) {
      let errors: ApiError[] = (err.errors as ApiError[]) || [];
      if (errors.some((e) => e.detail === 'first_name can not contain a url')) {
        this.alerts.error('signup.error', 'First name cannot contain a URL');
      } else if (errors.some((e) => e.detail === 'last_name can not contain a url')) {
        this.alerts.error('signup.error', 'Last name cannot contain a URL');
      } else {
        this.alerts.error('signup.error', 'Problem signing up. Try again later.');
      }
      throw err;
    }
  });

  setDefaults() {
    this.set('formValues', this.formValues);
  }

  trackSignupProgress(finishedStepRouteName?: SignupRoute | PersonalizationSignupRoute) {
    let {
      email,
      firstName,
      lastName,
      companyName,
      useCase,
      useCaseDetail,
      employeeCount,
      workPhone,
      acceptTerms,
      signupType,
      cdpPlatform,
      cdpPlatformOther,
    } = this.formValues;

    if (finishedStepRouteName === 'signup.index') {
      return this.signup.trackIdentify({
        email,
        firstName,
        lastName,
        startedSignupAt: moment().unix(),
        createdAt: moment().unix(),
      });
    }

    if (finishedStepRouteName === 'authenticated.post_registration.goal') {
      return this.signup.trackIdentify({
        email,
        useCase,
        useCaseDetail,
      });
    }

    if (finishedStepRouteName === 'signup.company') {
      return this.signup.trackIdentify({
        email,
        firstName,
        lastName,
        companyName,
        employeeCount,
        signupType,
        cdpPlatform,
        cdpPlatformOther,
        workPhone,
        acceptTerms,
      });
    }

    // Any other route (password, referral, etc.), we don't need to do anything.
    return;
  }

  @action
  signInClicked() {
    this.analytics.track('signup', 'sign_in_clicked', {
      signupType: this.model.signupType.name,
    });
  }

  @action
  requestInviteClicked() {
    this.analytics.track('signup', 'request_invite_clicked', {
      signupType: this.model.signupType.name,
    });
  }

  @action
  setFormValue<T extends keyof RegistrationAttrs>(key: T, value: RegistrationAttrs[T]) {
    this.formValues = { ...this.formValues, [key]: value };
  }

  @action
  getFormValue<T extends keyof RegistrationAttrs>(key: T) {
    return this.formValues[key];
  }
}

declare module '@ember/controller' {
  interface Registry {
    signup: SignupController;
  }
}
